package com.karyadina.ui.screens

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.karyadina.model.AppFeature
import com.karyadina.ui.getFeatureIcon
import com.karyadina.ui.theme.StudyIcons
import com.karyadina.ui.theme.StudyStrings
import com.karyadina.ui.theme.StudyTheme
import org.jetbrains.compose.resources.stringResource
import org.jetbrains.compose.resources.StringResource

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun HelpScreen(
    onBackClick: () -> Unit
) {
    Scaffold(
        topBar = {
            TopAppBar(
                title = { 
                    Column {
                        Text(stringResource(StudyStrings.HelpScreenTitle), fontWeight = FontWeight.Bold)
                        Text(stringResource(StudyStrings.HelpScreenDesc), style = MaterialTheme.typography.bodySmall)
                    }
                },
                navigationIcon = {
                    IconButton(onClick = onBackClick) {
                        Icon(Icons.AutoMirrored.Filled.ArrowBack, stringResource(StudyStrings.Back))
                    }
                },
                colors = TopAppBarDefaults.topAppBarColors(
                    containerColor = MaterialTheme.colorScheme.surface,
                    scrolledContainerColor = MaterialTheme.colorScheme.surfaceColorAtElevation(2.dp)
                )
            )
        }
    ) { padding ->
        val features = AppFeature.entries
        
        LazyColumn(
            modifier = Modifier
                .fillMaxSize()
                .background(MaterialTheme.colorScheme.surface)
                .padding(padding),
            contentPadding = PaddingValues(16.dp),
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            items(features) { feature ->
                HelpFeatureCard(feature)
            }
        }
    }
}

@Composable
fun HelpFeatureCard(feature: AppFeature) {
    val titleRes = getHelpTitle(feature)
    val descRes = getHelpDesc(feature)
    
    ElevatedCard(
        modifier = Modifier.fillMaxWidth(),
        colors = CardDefaults.elevatedCardColors(
            containerColor = MaterialTheme.colorScheme.surfaceContainerLow
        ),
        elevation = CardDefaults.elevatedCardElevation(defaultElevation = 1.dp)
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(16.dp),
            verticalAlignment = Alignment.CenterVertically // Standard Row attribute
        ) {
            Surface(
                shape = MaterialTheme.shapes.medium,
                color = MaterialTheme.colorScheme.secondaryContainer,
                modifier = Modifier.size(48.dp)
            ) {
                Box(contentAlignment = Alignment.Center) {
                    Icon(
                        imageVector = getFeatureIcon(feature),
                        contentDescription = null,
                        tint = MaterialTheme.colorScheme.onSecondaryContainer
                    )
                }
            }
            
            Spacer(modifier = Modifier.width(16.dp))
            
            Column {
                Text(
                    text = stringResource(titleRes),
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.Bold
                )
                Spacer(modifier = Modifier.height(4.dp))
                Text(
                    text = stringResource(descRes),
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
        }
    }
}

fun getHelpTitle(feature: AppFeature): StringResource {
    return when (feature) {
        AppFeature.IMAGE_TO_PDF -> StudyStrings.HelpFeaturePdfTitle
        AppFeature.IMAGE_RESIZER -> StudyStrings.HelpFeatureResizerTitle
        AppFeature.TRANSLATOR -> StudyStrings.HelpFeatureTranslatorTitle
        AppFeature.MAGIC_TEXT_SCANNER -> StudyStrings.HelpFeatureOcrTitle
        AppFeature.POMODORO_TIMER -> StudyStrings.HelpFeatureTimerTitle
        AppFeature.TASK_TRACKER -> StudyStrings.HelpFeatureTasksTitle
        AppFeature.STUDY_PLANNER -> StudyStrings.HelpFeaturePlannerTitle
        AppFeature.QUICK_NOTES -> StudyStrings.HelpFeatureNotesTitle
        AppFeature.FLASHCARDS -> StudyStrings.HelpFeatureFlashcardsTitle
    }
}

fun getHelpDesc(feature: AppFeature): StringResource {
    return when (feature) {
        AppFeature.IMAGE_TO_PDF -> StudyStrings.HelpFeaturePdfDesc
        AppFeature.IMAGE_RESIZER -> StudyStrings.HelpFeatureResizerDesc
        AppFeature.TRANSLATOR -> StudyStrings.HelpFeatureTranslatorDesc
        AppFeature.MAGIC_TEXT_SCANNER -> StudyStrings.HelpFeatureOcrDesc
        AppFeature.POMODORO_TIMER -> StudyStrings.HelpFeatureTimerDesc
        AppFeature.TASK_TRACKER -> StudyStrings.HelpFeatureTasksDesc
        AppFeature.STUDY_PLANNER -> StudyStrings.HelpFeaturePlannerDesc
        AppFeature.QUICK_NOTES -> StudyStrings.HelpFeatureNotesDesc
        AppFeature.FLASHCARDS -> StudyStrings.HelpFeatureFlashcardsDesc
    }
}
